import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import UserService from '../../../../Services/UserService';
import CustomerService from '../../../../Services/CustomerService';
import ClientService from '../../../../Services/ClientService';
import RecipientService from '../../../../Services/RecipientService';
import ClientTable from './ClientTable/ClientTable';
import DataPanel from '../../../../Components/DataPanel/DataPanel';
import XpektorButton from '../../../../Components/XpektorButton/XpektorButton';
import HandleAccessModal from '../../Modals/HandleAccessModal';
import CreateClientModal from '../../Modals/CreateClientModal';
import ConfirmActionModal from '../../Modals/ConfirmActionModal';
import UserClientAccessService from '../../../../Services/UserClientAccessService';
// import ExpandablePanel from '../../../../Components/ExpandablePanel/ExpandablePanel';

const gT = (t, key) => t(`page-admin-settings.client-settings${key}`);
const gsT = (t, key) => t(`shared${key}`);

/* eslint-disable react/prop-types, react/jsx-props-no-spreading, no-alert */
export default function ClientSettings({ users, userGroups, clients, t }) {
  const [statistics, setStatistics] = useState({});
  const [clientCount, setClientCount] = useState(0);
  const [somethingConnectedCount, setSomethingConnectedCount] = useState(0);
  const [accountingConnectedCount, setAccountingConnectedCount] = useState(0);
  const [bankConnectedCount, setBankConnectedCount] = useState(0);
  const [skvConnectedCount, setSkvConnectedCount] = useState(0);
  const [graphData, setGraphData] = useState([]);

  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedClient, setSelectedClient] = useState();
  const [customerId, setCustomerId] = useState();
  const { search } = useLocation();

  const [order, setOrder] = useState('asc');

  const [clientNotificationRecipients, setClientNotificationRecipients] =
    useState([]);
  const [fetchingRecipients, setFetchingRecipients] = useState(false);

  const [clientUserAssignments, setClientUserAssignments] = useState([]);
  const [clientGroupAssignments, setClientGroupAssignments] = useState([]);
  const [fetchingAssignments, setFetchingAssignments] = useState(false);

  const sortByName = () => {
    if (order === 'asc') {
      clients.sort((a, b) => {
        const nameA = a.name?.toUpperCase();
        const nameB = b.name?.toUpperCase();

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      setOrder('desc');
    } else if (order === 'desc') {
      clients.sort((a, b) => {
        const nameA = a.name?.toUpperCase();
        const nameB = b.name?.toUpperCase();

        if (nameA < nameB) {
          return 1;
        }
        if (nameA > nameB) {
          return -1;
        }
        return 0;
      });
      setOrder('asc');
    }
  };

  const sortByDate = () => {
    if (order === 'asc') {
      clients.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
      setOrder('desc');
    } else if (order === 'desc') {
      clients.sort((a, b) => new Date(a.dateAdded) - new Date(b.dateAdded));
      setOrder('asc');
    }
  };

  const fetchClientAssignments = async (clientId) => {
    setFetchingAssignments(true);
    await UserClientAccessService.GetClientUserAssignments(clientId)
      .then((response) => {
        setClientUserAssignments(response.data);
        return UserClientAccessService.GetClientUserGroupAssignments(clientId);
      })
      .then((response) => {
        setClientGroupAssignments(response.data);
      })
      .catch(() => {
        setClientUserAssignments([]);
        setClientGroupAssignments([]);
      })
      .finally(() => setFetchingAssignments(false));
  };

  useEffect(() => {
    const getData = async () => {
      const user = await UserService.getCurrentUser();
      setCustomerId(user.customerId);

      await CustomerService.getCustomerStatistics(user.customerId)
        .then((response) => {
          setStatistics(response.data);
          setClientCount(response.data.clientCount);
          setSomethingConnectedCount(response.data.anyConnectionCount);
          // setAnyAccessCount(response.data.anyAccessCount);
          setAccountingConnectedCount(response.data.accountingConnectionCount);
          setBankConnectedCount(response.data.bankConnectionCount);
          setSkvConnectedCount(response.data.skvConnectionCount);
          if (response.data.clientGraphData) {
            /* eslint-disable-next-line */
            setGraphData([
              {
                id: gsT(t, '.labels.clients'),
                data: response.data.clientGraphData.data,
              },
            ]);
          }
        })
        .catch(() => {});
    };
    const decodeAction = async () => {
      const queryAction = new URLSearchParams(search).get('action');
      const clientId = new URLSearchParams(search).get('clientId');
      if (!queryAction) return;

      let client = null;
      if (clientId) {
        await ClientService.getClient(clientId)
          .then((response) => {
            client = response.data;
            setSelectedClient(response.data);
          })
          .catch(() => {});
      }
      if (queryAction === 'clientAssignments') {
        fetchClientAssignments(client.id);
        setShowAccessModal(true);
      }
    };
    decodeAction();
    getData();
  }, [search, t]);

  const deleteClient = async () => {
    await ClientService.deleteClient(selectedClient.id, customerId)
      .then((response) => {
        if (response) {
          window.location = '/app/settings/clients';
        }
      })
      .catch((error) => {
        if (error.response.status === 404) {
          alert(gT(t, '.error.could-not-remove-client'));
        }
        if (error.response.status === 403) {
          alert(gT(t, '.error.forbidden-remove-client'));
        }
        if (error.response.status === 500) {
          alert(gT(t, '.error.general'));
        }
      });
  };

  const fetchNotificationRecipients = async (clientId) => {
    setFetchingRecipients(true);
    await RecipientService.GetRecipientsByClient(clientId)
      .then((response) => {
        setClientNotificationRecipients(
          response.data.map((recipient) => recipient.userId)
        );
      })
      .catch(() => setClientNotificationRecipients([]))
      .finally(() => setFetchingRecipients(false));
  };

  const saveNotificationRecipientChanges = async (changes) => {
    const promises = [];
    if (changes.users) {
      changes.users.addedIds.forEach((userId) => {
        promises.push(
          RecipientService.PostRecipient(userId, selectedClient.id)
        );
      });
      changes.users.removedIds.forEach((userId) => {
        promises.push(
          RecipientService.DeleteRecipient(userId, selectedClient.id)
        );
      });
    }
    await Promise.all(promises);
  };

  const saveAssignmentChanges = async (changes) => {
    const promises = [];

    if (changes.users) {
      changes.users.addedIds.forEach((userId) => {
        promises.push(
          UserClientAccessService.AssignClientToUser(selectedClient.id, userId)
        );
      });
      changes.users.removedIds.forEach((userId) => {
        promises.push(
          UserClientAccessService.UnassignClientFromUser(
            selectedClient.id,
            userId
          )
        );
      });
    }

    if (changes.groups) {
      changes.groups.addedIds.forEach((groupId) => {
        promises.push(
          UserClientAccessService.AssignClientToUserGroup(
            selectedClient.id,
            groupId
          )
        );
      });
      changes.groups.removedIds.forEach((groupId) => {
        promises.push(
          UserClientAccessService.UnassignClientFromUserGroup(
            selectedClient.id,
            groupId
          )
        );
      });
    }

    await Promise.all(promises);
    setClientUserAssignments([]);
    setClientGroupAssignments([]);
  };

  return (
    <div className="settings-content">
      <div className="panel-grid">
        <DataPanel
          title={gsT(t, '.labels.clients')}
          titleSize="m"
          centerContent
        >
          <DataPanel.ValueEntry
            label={gT(t, '.label.client-count')}
            value={clientCount}
          />
          <DataPanel.ValueEntry
            label={gT(t, '.label.clients-added-this-month')}
            value={
              statistics?.clientsAddedThisMonth ??
              gsT(t, '.labels.not-available')
            }
          />
          <DataPanel.ValueEntry
            label={gT(t, '.label.previous-clients-this-month')}
            value={
              statistics?.previousClientsThisMonth ??
              gsT(t, '.labels.not-available')
            }
          />
        </DataPanel>
        <DataPanel title={gT(t, '.title.clients-over-time')} titleSize="m">
          <DataPanel.LineChart graphData={graphData} />
        </DataPanel>
        <div className="panel-column">
          <DataPanel title={gT(t, '.title.connected-clients')} titleSize="s">
            <DataPanel.BarEntry
              text={gT(t, '.text.something-connected-count')}
              count={somethingConnectedCount}
              total={clientCount}
              height={32}
            />
          </DataPanel>
          <DataPanel title="Varav" titleSize="s">
            <DataPanel.BarEntry
              text={gsT(t, '.labels.accounting')}
              count={accountingConnectedCount}
              total={somethingConnectedCount}
              height={32}
            />
            <DataPanel.BarEntry
              text={gsT(t, '.labels.bank')}
              count={bankConnectedCount}
              total={somethingConnectedCount}
              height={32}
            />
            <DataPanel.BarEntry
              text={gsT(t, '.labels.skatteverket')}
              count={skvConnectedCount}
              total={somethingConnectedCount}
              height={32}
            />
          </DataPanel>
        </div>
      </div>
      {/*
      <ExpandablePanel title="Statistik efter användare">
        <div className="panel-grid">
          <DataPanel
            title="Klienter per skapare"
            titleSize="m"
            maxHeight={230}
            scroll
          >
            {statistics.userStatistics && statistics.userStatistics.length > 0
              ? [...statistics.userStatistics]
                  .sort((a, b) => b.clientsCreated - a.clientsCreated)
                  .map((userStats) => {
                    return (
                      <DataPanel.BarEntry
                        key={
                          userStats.userId ??
                          `unknown-user-stats-${userStats.userEmail}`
                        }
                        count={userStats.clientsCreated}
                        text={
                          userStats.userName ??
                          (userStats.userEmail !== 'Unknown'
                            ? userStats.userEmail
                            : 'Okänd användare')
                        }
                        total={clientCount}
                        height={36}
                      />
                    );
                  })
              : ''}
          </DataPanel>
          <DataPanel
            title="Klienter med någonting kopplat per skapare"
            titleSize="m"
            maxHeight={230}
            scroll
          >
            {statistics.userStatistics && statistics.userStatistics.length > 0
              ? [...statistics.userStatistics]
                  .sort((a, b) => b.anyConnectionCount - a.anyConnectionCount)
                  .map((userStats) => {
                    return (
                      <DataPanel.BarEntry
                        key={
                          userStats.userId ??
                          `unknown-user-stats-${userStats.userEmail}`
                        }
                        count={userStats.anyConnectionCount}
                        text={
                          userStats.userName ??
                          (userStats.userEmail !== 'Unknown'
                            ? userStats.userEmail
                            : 'Okänd användare')
                        }
                        total={userStats.clientsCreated}
                        height={36}
                      />
                    );
                  })
              : ''}
          </DataPanel>
          <DataPanel
            title="Klienter har/haft något kopplat per skapare"
            titleSize="m"
            maxHeight={230}
            scroll
          >
            {statistics.userStatistics && statistics.userStatistics.length > 0
              ? [...statistics.userStatistics]
                  .sort((a, b) => b.anyAccessCount - a.anyAccessCount)
                  .map((userStats) => {
                    return (
                      <DataPanel.BarEntry
                        key={
                          userStats.userId ??
                          `unknown-user-stats-${userStats.userEmail}`
                        }
                        count={userStats.anyAccessCount}
                        text={
                          userStats.userName ??
                          (userStats.userEmail !== 'Unknown'
                            ? userStats.userEmail
                            : 'Okänd användare')
                        }
                        total={userStats.clientsCreated}
                        height={36}
                      />
                    );
                  })
              : ''}
          </DataPanel>
        </div>
      </ExpandablePanel>
      */}
      <div className="row-container">
        <XpektorButton
          content={gT(t, '.xpektor-btn.create-client')}
          icon={faPlus}
          onClick={() => {
            setSelectedClient([]);
            setShowCreateClientModal(true);
          }}
        />
      </div>
      <ClientTable
        sortByName={sortByName}
        sortByDate={sortByDate}
        order={order}
        t={t}
      >
        {clients.map((data) => (
          <ClientTable.Item
            client={data}
            key={data.id}
            onNotificationClick={() => {
              setSelectedClient(data);
              fetchNotificationRecipients(data.id);
              setShowNotificationModal(true);
            }}
            onUserAccessClick={() => {
              setSelectedClient(data);
              fetchClientAssignments(data.id);
              setShowAccessModal(true);
            }}
            onDeleteClick={() => {
              setSelectedClient(data);
              setShowDeleteModal(true);
            }}
            notAvailable={gsT(t, '.labels.not-available')}
          />
        ))}
      </ClientTable>
      {showCreateClientModal && (
        <CreateClientModal
          handleClose={() => setShowCreateClientModal(false)}
          t={t}
        />
      )}
      {showNotificationModal && (
        <HandleAccessModal
          /* eslint-disable */
          modalTitle={`${gT(t, '.modal-title.notification')}: ${
            selectedClient.name
          }`}
          memberPanelTitle={gT(t, '.member-panel-title.notification')}
          handleClose={() => setShowNotificationModal(false)}
          data={[
            {
              type: 'users',
              all: users,
              members: clientNotificationRecipients,
            },
          ]}
          onSave={(changes) => saveNotificationRecipientChanges(changes)}
          loading={fetchingRecipients}
          t={t}
        />
      )}
      {showAccessModal && (
        <HandleAccessModal
          modalTitle={`${gT(t, '.modal-title.access')}: ${selectedClient.name}`}
          memberPanelTitle={gT(t, '.member-panel-title.access')}
          handleClose={() => setShowAccessModal(false)}
          data={[
            { type: 'users', all: users, members: clientUserAssignments },
            {
              type: 'groups',
              all: userGroups,
              members: clientGroupAssignments,
            },
          ]}
          onSave={(changes) => saveAssignmentChanges(changes)}
          loading={fetchingAssignments}
          t={t}
        />
      )}
      {showDeleteModal && (
        <ConfirmActionModal
          handleClose={() => setShowDeleteModal(false)}
          description={`${gT(t, '.delete.description')} ${
            selectedClient?.name
          }.`}
          actionButtonText={gsT(t, '.actions.delete')}
          onConfirm={deleteClient}
          danger
          t={t}
        />
      )}
    </div>
  );
}
